import React from "react";
import { Formik } from "formik";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { createMerchantAccountProducts } from "actions/product";
import {
  prepareCreateProducts,
  getCreateProductsInitialValues,
  validateProductsSchema
} from "utils/product";
import ProductNewList from "./ProductNewList";
import ImportProductPicker from "components/ImportProductPicker";
import { useCtxReferences, useIntegrations } from "utils/selectors";
import SERVICES from "utils/constants/services";
import Tabs from "components/Tabs";
import { FORM_KEY, FORM_MODE } from "utils/constants/form";
import { WHITE_TAB_STYLES } from "components/Tabs";
import startCase from "lodash/startCase";
import { useRouter } from "next/router";
import { usePrevPathFragment } from "utils/hooks/hub";
import { useScrollTopOnMount, useTabPathPrefix } from "utils/hooks";
import { getTabsProps } from "components/Tabs";

const ProductCreate = ({ actions, header, afterCreate }) => {
  useScrollTopOnMount();
  const backPath = usePrevPathFragment();
  const { resourceUUID, resource, merchantAccountUUID } = useCtxReferences();
  const hasIntegrations =
    useIntegrations().filter(
      ({ service }) =>
        [SERVICES.SHOPIFY, SERVICES.SQUARESPACE].indexOf(service) > -1
    ).length > 0;

  const { replace } = useRouter();
  const tabPrefix = useTabPathPrefix();

  const goShow = (id) => {
    replace(`${backPath}/${id}`);
  };

  const goBack = () => {
    replace(backPath);
  };

  const submitForm = (formValues, formikActions) => {
    const data = prepareCreateProducts(formValues.products);

    actions.createMerchantAccountProducts(
      {
        resourceUUID,
        resource,
        uuid: merchantAccountUUID,
        data
      },
      /**
       * Returns destructured collections of products and prices
       * @param {*} err
       * @param {Object<{prices: Array, products: Array}>} responseData
       */
      (err, responseData) => {
        /**
         * 1. Turn off loader
         */
        formikActions.setSubmitting(false);
        /**
         * 2. Show errors or reset form
         */
        if (err) {
          formikActions.setErrors(err);
        } else {
          formikActions.resetForm({ values: formValues });
        }
        /**
         * 3a. Call afterCreate handler or
         * 3b. Perform default conditional navigation
         */
        if (afterCreate) {
          afterCreate(err, responseData);
        } else {
          /**
           * If creating multiple - redirect to index
           * If creating single - redirect to show
           */
          if (
            responseData &&
            responseData.products &&
            responseData.products.length
          ) {
            responseData.products.length > 1
              ? replace(backPath)
              : goShow(responseData.products[0].id);
          } else {
            replace(backPath);
          }
        }
      }
    );
  };

  return (
    <Formik
      initialValues={getCreateProductsInitialValues()}
      validate={validateProductsSchema}
      onSubmit={submitForm}
    >
      {({ setFieldValue }) => {
        const tabProps = {
          ...getTabsProps(tabPrefix),
          onUpdate: (selection) => {
            setFieldValue(`${FORM_KEY}.mode`, selection);
          },
          tabs: [
            {
              name: startCase(FORM_MODE.CREATE),
              id: FORM_MODE.CREATE,
              node: <ProductNewList name="products" header={header} />
            }
          ]
        };

        if (hasIntegrations) {
          tabProps.tabs.push({
            name: startCase(FORM_MODE.IMPORT),
            id: FORM_MODE.IMPORT,
            node: <ImportProductPicker goBack={goBack} />
          });
        }

        return <Tabs {...tabProps} />;
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createMerchantAccountProducts
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ProductCreate);
